import React, { FC, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { SnackbarProvider } from 'notistack';
import './assets/styles/maine.scss';
import { BrowserRouter } from 'react-router-dom';
import { AppRouter } from './router';
import { useAppDispatch, useAppSelector } from './hooks/redux';
// import { LiveChat } from './components/commons/Chats';
import { langSlice } from './store/reducers/LangSlice';
import Wss from './core/pusher/Pusher';
import NotificationProvider from './providers/NotificationProvider';
import { availableLanguages, defaultLanguage } from './constants/configuration';
import { userInfoApi } from './services/apiServices/user/userInfo';
import { userPushNotificationsApi } from './services/apiServices/user/userPushNotifications';
import { NotificationsApi } from './models/PushNotification';
import { formatLocale } from './utils';
import { useLogRocket } from './hooks/useLogRocket';
import LogRocketLabel from './components/LogRocketLabel';

const App: FC = () => {
  const { theme } = useAppSelector((state) => state.appReducer);
  const { userInfoState, isUserInfoDownloaded } = useAppSelector((state) => state.userInfoReducer);
  const { i18n } = useTranslation();
  const dispatch = useAppDispatch();
  const { selectLanguage } = langSlice.actions;
  const [getAppSettings] = userInfoApi.useLazyGetAppSettingsQuery();
  const [getMessages, { data: pushNotificationsData, isFetching: isNotificationsLoading }] =
    userPushNotificationsApi.useLazyGetNotificationsQuery();
  const [pushNotifications, setPushNotifications] = useState(pushNotificationsData?.data || []);

  const { isShowRocketLogMode } = useLogRocket();

  const notificationsApi: NotificationsApi = {
    getMessages,
    pushNotifications,
    isNotificationsLoading,
    nextPageUrl: pushNotificationsData?.next_page_url ?? null,
    prevPageUrl: pushNotificationsData?.prev_page_url ?? null,
  };

  const updateQueryLang = (language: string) => {
    const { location, history } = window;
    const queryParams = new URLSearchParams(location.search);
    if (queryParams.has('lang')) {
      queryParams.set('lang', language);
      history.replaceState({}, '', `?${queryParams.toString()}`);
    }
  };

  useEffect(() => {
    Wss.connect();
    getAppSettings('');
  }, []);

  useEffect(() => {
    if (pushNotificationsData?.data) {
      if (pushNotificationsData.current_page !== 1) {
        setPushNotifications((prev) => {
          const next = pushNotificationsData.data.filter((i) => !prev.find((p) => p.id === i.id));
          return [...prev, ...next].sort((a, b) => (a.id < b.id ? 1 : -1));
        });
      } else {
        setPushNotifications(pushNotificationsData.data);
      }
    }
  }, [pushNotificationsData?.data]);

  useEffect(() => {
    document.body.className = `body__${theme}`;
  }, [theme]);

  useEffect(() => {
    if (isUserInfoDownloaded) {
      const userLanguage = availableLanguages.includes(userInfoState.Language)
        ? userInfoState.Language
        : defaultLanguage;
      if (formatLocale(i18n.language) !== formatLocale(userLanguage)) {
        updateQueryLang(userLanguage);
        dispatch(selectLanguage(userLanguage));
        window.location.reload();
      }
    } else {
      dispatch(selectLanguage(i18n.language));
    }
  }, [isUserInfoDownloaded]);

  return (
    <BrowserRouter>
      <div>
        <SnackbarProvider maxSnack={2}>
          <NotificationProvider refetchPushes={getMessages}>
            <AppRouter {...notificationsApi} />
          </NotificationProvider>
        </SnackbarProvider>
      </div>
      {/* <LiveChat /> */}
      <LogRocketLabel isShow={isShowRocketLogMode} />
    </BrowserRouter>
  );
};

export default App;
